import React from 'react';

import { ButtonTag } from '@/components/shared/buttons/button-tag';
import { Link } from '@/components/shared/element/link';
import { Text } from '@/components/shared/element/text';
import { Box } from '@/components/shared/layout/box';
import { Flex } from '@/components/shared/layout/flex';
import { Grid } from '@/components/shared/layout/grid';
import { Image } from '@/components/shared/media/image';
import { generateTabletAndMobileImageSources } from '@/components/shared/media/image/generate-sources';
import { formatDateFull, removeSpan } from '@/components/shared/utility/format';
import { MENU_LCV_ABOUT_NEWS } from '@/contents/lcv/link';
import { INewsItem } from '@/types/news/news';

interface NewsHighlightItemProps {
  highlight: INewsItem[];
  dataTest?: string;
  dataTrack?: string;
}

const NewsHighlightItem: React.FC<NewsHighlightItemProps> = ({
  dataTrack = 'news-list',
  dataTest = 'newsList',
  ...props
}) => {
  return (
    <>
      {props.highlight.map((o, i) => (
        <Box
          key={i}
          component="article"
          data-test="news_highlight"
          test={{
            dataTest: 'news_highlight',
          }}
        >
          <Grid
            templateColumns={{ '@lg': '3' }}
            alignItems="start"
            columnGap={{ '@initial': '10', '@xl': '12' }}
            rowGap="4"
          >
            {!!o.image && !!o.image.src && (
              <Link
                href={o.url}
                tracking={{
                  dataTrack: dataTrack,
                  dataTrackText: o.title,
                  dataTrackUrl: o.url,
                }}
                test={{
                  dataTest: `${dataTest}_link`,
                }}
                css={{ '@lg': { gridColumn: '1 / span 2' } }}
              >
                <Image
                  priority
                  placeholder="shimmer"
                  hoverEffect="zoom"
                  src={o.image.src}
                  alt={o.image.alt}
                  width={600}
                  height={338}
                  layout="responsive"
                  aspectRatio="16by9"
                  sources={[
                    {
                      srcset: o.image.src,
                      media: { minWidth: 1024 },
                      options: { width: 828, crop: '16:9' },
                    },
                    ...generateTabletAndMobileImageSources({
                      mobileSrc: o.image.src,
                      mobileOptions: {
                        crop: '16:9',
                      },
                    }),
                  ]}
                />
              </Link>
            )}
            <Box paddingTop={{ '@lg': '10' }}>
              <Grid rowGap={{ '@initial': '2', '@md': '4', '@lg': '6' }}>
                {!!o.title && !!o.beginDate && (
                  <Grid rowGap="3">
                    {!!o.beginDate && (
                      <Flex alignItems="center" columnGap="2">
                        {!!o.category?.url && !!o.category.name && (
                          <ButtonTag
                            label={o.category.name}
                            href={`${MENU_LCV_ABOUT_NEWS.url}/${o.category.url}`}
                          />
                        )}
                        {!!o.beginDate && (
                          <Text
                            as="p"
                            size="sm"
                            variant="text-gray"
                            data-test={`${dataTest}_beginDate`}
                            css={{ marginBottom: -2 }}
                          >
                            {formatDateFull(o.beginDate)}
                          </Text>
                        )}
                      </Flex>
                    )}
                    {!!o.title && (
                      <Link
                        href={o.url}
                        tracking={{
                          dataTrack: dataTrack,
                          dataTrackText: o.title,
                          dataTrackUrl: o.url,
                        }}
                        test={{
                          dataTest: `${dataTest}_link`,
                        }}
                        hoverColor="red"
                      >
                        <Text
                          as="h3"
                          size={{ '@initial': 'h5', '@lg': 'h4' }}
                          font="bold"
                          wrap
                          dangerouslySetInnerHTML={{
                            __html: o.title,
                          }}
                          data-test={`${dataTest}_title`}
                        />
                      </Link>
                    )}
                  </Grid>
                )}
                {!!o.excerpt && (
                  <Text
                    className="news-excerpt"
                    as="div"
                    size={{ '@initial': 'text-body-1', '@lg': 'h5' }}
                    lines="4"
                    dangerouslySetInnerHTML={{
                      __html: removeSpan(o.excerpt),
                    }}
                    data-test={`${dataTest}_shortDescription`}
                  />
                )}
              </Grid>
            </Box>
          </Grid>
        </Box>
      ))}
    </>
  );
};
NewsHighlightItem.displayName = 'NewsHighlightItemSection';
export default NewsHighlightItem;
